import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, Clock3, Filter, RefreshCw, Save } from 'lucide-react'
import type {
  BenchmarkCurationAnnotationStatus,
  BenchmarkCurationPacketDetail,
} from '@/types/api'
import {
  useBenchmarkCurationOverview,
  useBenchmarkCurationPacket,
  useBenchmarkCurationPackets,
  useBenchmarkCurationQueue,
  useUpdateBenchmarkCurationPacket,
} from '@/hooks/useBenchmarkCuration'

const STATUS_OPTIONS: Array<BenchmarkCurationAnnotationStatus | 'all'> = [
  'all',
  'todo',
  'approved',
  'rejected',
  'adjudicated',
]

const STATUS_LABELS: Record<BenchmarkCurationAnnotationStatus | 'all', string> = {
  all: 'すべて',
  todo: '未対応',
  approved: '一次承認',
  rejected: '差し戻し',
  adjudicated: '確定',
}

const PRIORITY_LABELS = {
  temporal: '期限や時点が重要',
  business_key: '会社名や担当名が重要',
  claim_heavy: '答えに入れる要点が多い',
  standard: '標準',
} as const

const QUESTION_TYPE_LABELS: Record<string, string> = {
  decisions: '決定事項',
  actions: '次のアクション',
  summary: '要約',
  temporal: '期限・時点',
  pricing: '金額・条件',
  risk: '懸念点',
  next_step: '次の打ち手',
}

const QUEUE_ACTION_LABELS = {
  annotate: '新規対応',
  requeue: '再確認',
  hold: '保留',
} as const

type FormState = {
  status: BenchmarkCurationAnnotationStatus
  approvedQuestion: string
  referenceAnswer: string
  mandatoryClaims: string
  optionalClaims: string
  forbiddenClaims: string
  mustHitEvidence: string
  requiredFacts: string
  abstainCondition: string
  notes: string
}

export function BenchmarkCurationPage() {
  const [status, setStatus] = useState<BenchmarkCurationAnnotationStatus | 'all'>('todo')
  const [query, setQuery] = useState('')
  const [selectedPacketId, setSelectedPacketId] = useState('')
  const [form, setForm] = useState<FormState | null>(null)

  const overviewQuery = useBenchmarkCurationOverview()
  const queueQuery = useBenchmarkCurationQueue(20, false)
  const packetsQuery = useBenchmarkCurationPackets({
    status,
    q: query,
    limit: 50,
    offset: 0,
  })
  const detailQuery = useBenchmarkCurationPacket(selectedPacketId)
  const updateMutation = useUpdateBenchmarkCurationPacket(selectedPacketId)

  useEffect(() => {
    const firstPacketId = packetsQuery.data?.items[0]?.packetId ?? ''
    if (!selectedPacketId && firstPacketId) {
      setSelectedPacketId(firstPacketId)
    }
    if (
      selectedPacketId &&
      packetsQuery.data &&
      !packetsQuery.data.items.some((item) => item.packetId === selectedPacketId)
    ) {
      setSelectedPacketId(firstPacketId)
    }
  }, [packetsQuery.data, selectedPacketId])

  useEffect(() => {
    if (!detailQuery.data?.packet) return
    setForm(toFormState(detailQuery.data.packet))
  }, [detailQuery.data?.packet])

  const selectedQueueInfo = detailQuery.data?.queueMeta
  const selectedPacketGuide = detailQuery.data?.packet
    ? buildPacketGuide(detailQuery.data.packet, detailQuery.data.queueMeta?.priorityBand ?? 'standard')
    : null
  const summaryCards = useMemo(() => {
    const breakdown = overviewQuery.data?.statusBreakdown
    return [
      { label: '未対応', value: breakdown?.todo ?? 0 },
      { label: '一次承認', value: breakdown?.approved ?? 0 },
      { label: '差し戻し', value: breakdown?.rejected ?? 0 },
      { label: '確定', value: breakdown?.adjudicated ?? 0 },
    ]
  }, [overviewQuery.data])

  const handleSave = async () => {
    if (!selectedPacketId || !form) return
    await updateMutation.mutateAsync({
      status: form.status,
      approvedQuestion: form.approvedQuestion,
      referenceAnswer: form.referenceAnswer,
      mandatoryClaims: parseMultiline(form.mandatoryClaims),
      optionalClaims: parseMultiline(form.optionalClaims),
      forbiddenClaims: parseMultiline(form.forbiddenClaims),
      mustHitEvidence: parseMultiline(form.mustHitEvidence),
      requiredFacts: parseMultiline(form.requiredFacts),
      abstainCondition: form.abstainCondition,
      notes: form.notes,
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ベンチマーク整備</h1>
          <p className="text-muted-foreground">
            CANAI の実データから作った packet を人手で整えて、real-curated benchmark 用の質問と正解条件を確定する画面です。
          </p>
        </div>
        <button
          onClick={() => {
            void overviewQuery.refetch()
            void queueQuery.refetch()
            void packetsQuery.refetch()
            if (selectedPacketId) {
              void detailQuery.refetch()
            }
          }}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
        >
          <RefreshCw className="h-4 w-4" />
          更新
        </button>
      </div>

      <section className="rounded-xl border bg-card p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
            CANAIガイド
          </div>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              この画面でやることは「実際に聞かれそうな質問」と「その答えに絶対必要な事実」を固めることです。
              文章をきれいに書くより、採点の基準がぶれないようにする方を優先してください。
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              <GuideCard
                title="1. 質問を自然にする"
                body="approvedQuestion は実際の担当者がそのまま聞きそうな言い回しにします。内部用語や評価都合の言い回しは避けます。"
              />
              <GuideCard
                title="2. 必須事実を絞る"
                body="mandatoryClaims と requiredFacts には、欠けると誤答になる事実だけを入れます。補足情報は optionalClaims へ回します。"
              />
              <GuideCard
                title="3. 断定条件を決める"
                body="根拠が弱いときに断定させないため、abstainCondition に『どこまで分かれば答えてよいか』を書きます。"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-5">
        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm text-muted-foreground">案件数</div>
          <div className="mt-2 text-3xl font-semibold">{overviewQuery.data?.caseCount ?? '-'}</div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm text-muted-foreground">packet数</div>
          <div className="mt-2 text-3xl font-semibold">{overviewQuery.data?.packetCount ?? '-'}</div>
        </div>
        {summaryCards.map((card) => (
          <div key={card.label} className="rounded-lg border bg-card p-4">
            <div className="text-sm text-muted-foreground">{card.label}</div>
            <div className="mt-2 text-3xl font-semibold">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
        <section className="space-y-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <Filter className="h-4 w-4" />
              絞り込み
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">状態</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as BenchmarkCurationAnnotationStatus | 'all')}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {STATUS_LABELS[option]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">検索</label>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="質問文 / scenario / 事実"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-medium">優先キュー</div>
              <div className="text-xs text-muted-foreground">
                {queueQuery.data?.report.includedPackets ?? 0} 件
              </div>
            </div>
            <div className="space-y-2">
              {queueQuery.data?.queue.map((entry) => (
                <button
                  key={entry.packetId}
                  onClick={() => setSelectedPacketId(entry.packetId)}
                  className="w-full rounded-md border p-3 text-left hover:bg-muted"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      #{entry.rank} · {PRIORITY_LABELS[entry.priorityBand]}
                    </span>
                    <span className="text-xs text-muted-foreground">{STATUS_LABELS[entry.currentStatus]}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium">{entry.approvedQuestion}</div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {QUEUE_ACTION_LABELS[entry.queueAction]} / {entry.reasons.join(' / ')}
                  </div>
                </button>
              ))}
              {!queueQuery.data?.queue.length && (
                <p className="text-sm text-muted-foreground">優先キューは空です。</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 text-sm font-medium">packet 一覧</div>
            <div className="space-y-2">
              {packetsQuery.data?.items.map((item) => (
                <button
                  key={item.packetId}
                  onClick={() => setSelectedPacketId(item.packetId)}
                  className={`w-full rounded-md border p-3 text-left ${
                    selectedPacketId === item.packetId ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-muted-foreground">
                      {PRIORITY_LABELS[item.priorityBand]} · {STATUS_LABELS[item.currentStatus]}
                    </div>
                    <div className="text-xs text-muted-foreground">優先度 {item.priorityScore}</div>
                  </div>
                  <div className="mt-1 text-sm font-medium">{item.approvedQuestion}</div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    必須claim {item.mandatoryClaimCount} · 必須fact {item.requiredFactCount} · 必須根拠 {item.mustHitEvidenceCount}
                  </div>
                </button>
              ))}
              {!packetsQuery.data?.items.length && (
                <p className="text-sm text-muted-foreground">該当 packet がありません。</p>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-lg border bg-card p-5">
          {!detailQuery.data?.packet || !form ? (
            <div className="flex min-h-[400px] items-center justify-center text-muted-foreground">
              packet を選ぶと詳細が表示されます
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground">{detailQuery.data.packet.packetId}</div>
                  <h2 className="text-xl font-semibold">{detailQuery.data.packet.annotation.approvedQuestion}</h2>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <Badge>{detailQuery.data.packet.sourceType}</Badge>
                    <Badge>{detailQuery.data.packet.privacyTag}</Badge>
                    <Badge>{QUESTION_TYPE_LABELS[detailQuery.data.packet.candidate.questionType] ?? detailQuery.data.packet.candidate.questionType}</Badge>
                    {selectedQueueInfo ? <Badge>{PRIORITY_LABELS[selectedQueueInfo.priorityBand]}</Badge> : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((current) => current ? { ...current, status: e.target.value as BenchmarkCurationAnnotationStatus } : current)
                    }
                    className="rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    {STATUS_OPTIONS.filter((option) => option !== 'all').map((option) => (
                      <option key={option} value={option}>
                        {STATUS_LABELS[option]}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => void handleSave()}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    保存
                  </button>
                </div>
              </div>

              {updateMutation.isSuccess && (
                <div className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" />
                  保存しました
                </div>
              )}
              {updateMutation.isError && (
                <div className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                  <AlertCircle className="h-4 w-4" />
                  保存に失敗しました
                </div>
              )}

              {selectedPacketGuide ? (
                <div className="rounded-lg border bg-sky-50 p-4 text-sm text-sky-950">
                  <div className="mb-2 font-semibold">{selectedPacketGuide.title}</div>
                  <p className="mb-2 text-sm">{selectedPacketGuide.body}</p>
                  <ul className="space-y-1 text-sm">
                    {selectedPacketGuide.checks.map((check, index) => (
                      <li key={index}>- {check}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="rounded-lg border bg-amber-50 p-4 text-sm text-amber-900">
                <div className="mb-2 font-semibold">この packet で直すポイント</div>
                <ul className="space-y-1 text-sm">
                  <li>1. 質問文: 実際にユーザーがそのまま聞くか</li>
                  <li>2. 必須の要点: これが抜けたら誤答になるか</li>
                  <li>3. 回答に必ず入れる事実: 最終回答に入っていないと困るか</li>
                  <li>4. 必ず拾いたい根拠: 検索で見つからないと困る根拠か</li>
                  <li>5. 保留条件: 情報不足のときに断定しない条件が明記されているか</li>
                </ul>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="確定質問文">
                  <p className="text-xs text-muted-foreground">実ユーザーが聞きそうな自然な文にします。</p>
                  <textarea
                    value={form.approvedQuestion}
                    onChange={(e) => setFormField(setForm, 'approvedQuestion', e.target.value)}
                    className="min-h-[88px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="基準回答">
                  <p className="text-xs text-muted-foreground">採点の基準になる短い正答です。文章のうまさより主要事実を優先します。</p>
                  <textarea
                    value={form.referenceAnswer}
                    onChange={(e) => setFormField(setForm, 'referenceAnswer', e.target.value)}
                    className="min-h-[88px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="必須の要点">
                  <p className="text-xs text-muted-foreground">欠けると誤答になる要点だけを 1 行 1 件で入れます。</p>
                  <textarea
                    value={form.mandatoryClaims}
                    onChange={(e) => setFormField(setForm, 'mandatoryClaims', e.target.value)}
                    className="min-h-[132px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="回答に必ず入れる事実">
                  <p className="text-xs text-muted-foreground">最終回答に含めてほしい具体事実です。採点の主軸になります。</p>
                  <textarea
                    value={form.requiredFacts}
                    onChange={(e) => setFormField(setForm, 'requiredFacts', e.target.value)}
                    className="min-h-[132px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="必ず拾いたい根拠">
                  <p className="text-xs text-muted-foreground">検索で絶対拾ってほしい根拠文を 1 行 1 件で入れます。</p>
                  <textarea
                    value={form.mustHitEvidence}
                    onChange={(e) => setFormField(setForm, 'mustHitEvidence', e.target.value)}
                    className="min-h-[132px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="書いてはいけない内容">
                  <p className="text-xs text-muted-foreground">これを答えに入れたら誤答扱いにしたい内容です。</p>
                  <textarea
                    value={form.forbiddenClaims}
                    onChange={(e) => setFormField(setForm, 'forbiddenClaims', e.target.value)}
                    className="min-h-[132px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="入っていると望ましい要点">
                  <p className="text-xs text-muted-foreground">入っていると望ましいが、なくても失格にしない情報です。</p>
                  <textarea
                    value={form.optionalClaims}
                    onChange={(e) => setFormField(setForm, 'optionalClaims', e.target.value)}
                    className="min-h-[132px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="保留条件">
                  <p className="text-xs text-muted-foreground">どこまで根拠がそろえば答えてよいか、足りなければ何を断定しないかを書きます。</p>
                  <textarea
                    value={form.abstainCondition}
                    onChange={(e) => setFormField(setForm, 'abstainCondition', e.target.value)}
                    className="min-h-[132px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </Field>
              </div>

              <Field label="メモ">
                <textarea
                  value={form.notes}
                  onChange={(e) => setFormField(setForm, 'notes', e.target.value)}
                  className="min-h-[120px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </Field>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="rounded-md border bg-background p-4">
                  <div className="mb-2 text-sm font-medium">匿名化済み原文</div>
                  <pre className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {detailQuery.data.packet.redactedText}
                  </pre>
                </div>
                <div className="space-y-4">
                  <div className="rounded-md border bg-background p-4">
                    <div className="mb-2 text-sm font-medium">候補ヒント</div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {detailQuery.data.packet.candidate.evidenceSnippets.map((snippet, index) => (
                        <li key={index}>- {snippet}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-md border bg-background p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                      <Clock3 className="h-4 w-4" />
                      優先キュー情報
                    </div>
                    {selectedQueueInfo ? (
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>対応種別: {QUEUE_ACTION_LABELS[selectedQueueInfo.queueAction]}</li>
                        <li>この packet の重要ポイント: {PRIORITY_LABELS[selectedQueueInfo.priorityBand]}</li>
                        <li>優先度: {selectedQueueInfo.priorityScore}</li>
                        {selectedQueueInfo.reasons.map((reason, index) => (
                          <li key={index}>- {reason}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">優先情報はありません。</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  )
}

function GuideCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="mb-2 text-sm font-semibold">{title}</div>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  )
}

function buildPacketGuide(
  packet: BenchmarkCurationPacketDetail,
  priorityBand: 'temporal' | 'business_key' | 'claim_heavy' | 'standard',
): {
  title: string
  body: string
  checks: string[]
} {
  const questionType = packet.candidate.questionType

  if (priorityBand === 'temporal' || questionType === 'temporal') {
    return {
      title: 'この packet は「期限や時点」が重要です',
      body:
        'この質問では、いつの情報なのかが答えの中心です。日付、締切、次回まで、ある時点以前の最新情報などがずれると誤答になります。',
      checks: [
        '日付や締切が本文に本当に書かれているか',
        '古い情報と新しい情報を取り違えていないか',
        '不明なのに勝手に期限を補っていないか',
      ],
    }
  }

  if (priorityBand === 'business_key') {
    return {
      title: 'この packet は「会社名・担当者名・案件名」などの固有情報が重要です',
      body:
        'この質問では、誰の話か、どの会社か、どの案件かがずれると意味が変わります。似た話題でも対象が違えば誤答です。',
      checks: [
        '会社名や担当者名が正しいか',
        '別案件の情報が混ざっていないか',
        '固有名詞が抜けても意味が保てるかを確認したか',
      ],
    }
  }

  if (priorityBand === 'claim_heavy') {
    return {
      title: 'この packet は「答えに入れるべき要点」が多いです',
      body:
        'この質問では、答えに入れるべき内容が複数あります。1つでも大事な要点が抜けると、正しそうに見えても不十分な回答になります。',
      checks: [
        '欠けると困る要点だけが「必須の要点」に入っているか',
        '補足情報を必須扱いしすぎていないか',
        '余計な断定を防ぐための保留条件があるか',
      ],
    }
  }

  return {
    title: 'この packet は標準的な確認で進められます',
    body:
      '質問文が自然か、答えに必要な要点が揃っているか、情報が足りないときに断定しないかを確認してください。',
    checks: [
      '質問文が自然であること',
      '答えに必要な要点が揃っていること',
      '情報不足時の保留条件があること',
    ],
  }
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border px-2 py-1 text-xs text-muted-foreground">
      {children}
    </span>
  )
}

function toFormState(packet: BenchmarkCurationPacketDetail): FormState {
  return {
    status: packet.annotation.status,
    approvedQuestion: packet.annotation.approvedQuestion,
    referenceAnswer: packet.annotation.referenceAnswer,
    mandatoryClaims: packet.annotation.mandatoryClaims.join('\n'),
    optionalClaims: packet.annotation.optionalClaims.join('\n'),
    forbiddenClaims: packet.annotation.forbiddenClaims.join('\n'),
    mustHitEvidence: packet.annotation.mustHitEvidence.join('\n'),
    requiredFacts: packet.annotation.requiredFacts.join('\n'),
    abstainCondition: packet.annotation.abstainCondition,
    notes: packet.annotation.notes,
  }
}

function parseMultiline(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function setFormField<K extends keyof FormState>(
  setForm: React.Dispatch<React.SetStateAction<FormState | null>>,
  key: K,
  value: FormState[K],
) {
  setForm((current) => (current ? { ...current, [key]: value } : current))
}
